/* RESPONSIBLE TEAM: team-reporting */

import { ChartSchema, ChartDefinition, BannerBlock } from '@/features/report-snapshots/api/schema';
import { cn } from '@intercom/surge';
import { sanitizeHtml } from '@intercom/intercom-sanitizer';
import { useEffect, useRef } from 'react';

interface BannerProps {
  chartSchema?: ChartSchema;
  chartDefinition: ChartDefinition;
}

function renderBannerBlock(block: BannerBlock, index: number) {
  const baseClasses = block.class ?? '';
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  };

  const alignClass = block.align ? alignmentClasses[block.align] : '';

  const SanitizedContent = ({ text }: { text: string }) => {
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      if (ref.current) {
        const sanitizedHtml = sanitizeHtml(text);
        // eslint-disable-next-line no-unsanitized/property -- Safe to use with sanitizeHtml from @intercom/intercom-sanitizer
        ref.current.innerHTML = sanitizedHtml;
      }
    }, [text]);

    return <span ref={ref} />;
  };

  const renderContent = (text: string): React.ReactNode => {
    return <SanitizedContent text={text} />;
  };

  const commonClasses = cn('block', baseClasses, alignClass);

  switch (block.type) {
    case 'heading':
      return (
        <h1
          key={index}
          id={block.idAttribute}
          className={cn('text-h1 font-bold mb-3', commonClasses)}
        >
          {renderContent(block.text)}
        </h1>
      );
    case 'subheading':
      return (
        <h2
          key={index}
          id={block.idAttribute}
          className={cn('text-h2 font-bold mb-2', commonClasses)}
        >
          {renderContent(block.text)}
        </h2>
      );
    case 'subheading3':
      return (
        <h3
          key={index}
          id={block.idAttribute}
          className={cn('text-h3 font-bold mb-2', commonClasses)}
        >
          {renderContent(block.text)}
        </h3>
      );
    case 'paragraph':
    default:
      return (
        <p key={index} id={block.idAttribute} className={cn('text-base', commonClasses)}>
          {renderContent(block.text)}
        </p>
      );
  }
}

function DescriptionContent({ description }: { description: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const sanitizedHtml = sanitizeHtml(description);
      // eslint-disable-next-line no-unsanitized/property -- Safe to use with sanitizeHtml from @intercom/intercom-sanitizer
      ref.current.innerHTML = sanitizedHtml;
    }
  }, [description]);

  return <div ref={ref} className="text-base" />;
}

export function Banner(props: BannerProps) {
  const { chartDefinition } = props;

  const bannerComponent = chartDefinition.visualizationOptions.component;

  if (!bannerComponent) {
    return null;
  }

  const hasBlocks = bannerComponent.blocks.length > 0;
  const description = chartDefinition.description;

  // If no content to render, return null
  if (!hasBlocks && !description) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute inset-0 flex flex-col p-6 space-y-2 rounded-3xl',
        bannerComponent.color,
      )}
    >
      {hasBlocks
        ? bannerComponent.blocks.map((block, index) => renderBannerBlock(block, index))
        : description && <DescriptionContent description={description} />}
    </div>
  );
}
